import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  canCreateEvent, 
  canAddGuest, 
  canAddRecipe, 
  canAddInventoryItem,
  canCreateGuestGroup,
  canCreateShoppingList,
  canAddWishlistItem 
} from '../services/limitsService';
import { getUserUsage } from '../services/userRecordsService';
import { FEATURE_LIMITS } from '../constants/featureLimits';
import { LimitCheckResult, UserUsage, FeatureLimits } from '../types/limits';

interface UseLimitsReturn {
  // Verificações de limite
  canCreateEvent: boolean;
  canAddGuest: boolean;
  canAddRecipe: boolean;
  canAddInventoryItem: boolean;
  canCreateGuestGroup: boolean;
  canCreateShoppingList: boolean;
  canAddWishlistItem: boolean;
  
  // Contadores restantes
  eventsRemaining: number;
  guestsRemaining: number;
  recipesRemaining: number;
  inventoryRemaining: number;
  groupsRemaining: number;
  shoppingListsRemaining: number;
  wishlistRemaining: number;
  
  // Estado
  isLoading: boolean;
  usage: UserUsage | null;
  
  // Ações
  refreshLimits: () => Promise<void>;
  showUpgradePrompt: (feature: string) => void;
  
  // Funções de verificação assíncrona
  checkEventLimit: () => Promise<LimitCheckResult>;
  checkGuestLimit: () => Promise<LimitCheckResult>;
  checkRecipeLimit: () => Promise<LimitCheckResult>;
  checkInventoryLimit: () => Promise<LimitCheckResult>;
  
  // Feature limits
  featureLimits: FeatureLimits;
}

export const useLimits = (): UseLimitsReturn => {
  const { user } = useAuth();
  const { plan } = useSubscription();
  
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Estados de verificação de limite
  const [limits, setLimits] = useState({
    canCreateEvent: true,
    canAddGuest: true,
    canAddRecipe: true,
    canAddInventoryItem: true,
    canCreateGuestGroup: true,
    canCreateShoppingList: true,
    canAddWishlistItem: true,
  });

  const refreshLimits = async () => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Buscar usage atual
      const currentUsage = await getUserUsage(user.uid);
      setUsage(currentUsage);

      if (!currentUsage) return;

      // Verificar todos os limites
      const [
        eventCheck,
        guestCheck,
        recipeCheck,
        inventoryCheck,
        groupCheck,
        shoppingListCheck,
        wishlistCheck,
      ] = await Promise.all([
        canCreateEvent(user.uid),
        canAddGuest(user.uid),
        canAddRecipe(user.uid),
        canAddInventoryItem(user.uid),
        canCreateGuestGroup(user.uid),
        canCreateShoppingList(user.uid),
        canAddWishlistItem(user.uid),
      ]);

      setLimits({
        canCreateEvent: eventCheck.allowed,
        canAddGuest: guestCheck.allowed,
        canAddRecipe: recipeCheck.allowed,
        canAddInventoryItem: inventoryCheck.allowed,
        canCreateGuestGroup: groupCheck.allowed,
        canCreateShoppingList: shoppingListCheck.allowed,
        canAddWishlistItem: wishlistCheck.allowed,
      });
    } catch (error) {
      console.error('Erro ao atualizar limites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshLimits();
  }, [user?.uid, plan]);

  // Calcular contadores restantes
  const planLimits = FEATURE_LIMITS[plan] || FEATURE_LIMITS.free;
  
  const calculateRemaining = (current: number, limit: number): number => {
    if (limit === -1) return -1; // Ilimitado
    return Math.max(0, limit - current);
  };

  const eventsRemaining = usage ? calculateRemaining(usage.eventsThisMonth, planLimits.eventsPerMonth) : 0;
  const guestsRemaining = usage ? calculateRemaining(usage.totalGuests, planLimits.totalGuests) : 0;
  const recipesRemaining = usage ? calculateRemaining(usage.totalRecipes, planLimits.totalRecipes) : 0;
  const inventoryRemaining = usage ? calculateRemaining(usage.totalInventoryItems, planLimits.totalInventoryItems) : 0;
  const groupsRemaining = usage ? calculateRemaining(usage.guestGroups, planLimits.guestGroups) : 0;
  const shoppingListsRemaining = usage ? calculateRemaining(usage.activeShoppingLists, planLimits.activeShoppingLists) : 0;
  const wishlistRemaining = usage ? calculateRemaining(usage.wishlistItems, planLimits.wishlistItems) : 0;

  const showUpgradePrompt = (feature: string) => {
    // Esta função será implementada pelos componentes que a usam
    console.log(`Mostrar prompt de upgrade para: ${feature}`);
  };

  // Funções de verificação assíncrona
  const checkEventLimit = () => canCreateEvent(user?.uid || '');
  const checkGuestLimit = () => canAddGuest(user?.uid || '');
  const checkRecipeLimit = () => canAddRecipe(user?.uid || '');
  const checkInventoryLimit = () => canAddInventoryItem(user?.uid || '');

  return {
    // Verificações de limite
    ...limits,
    
    // Contadores restantes
    eventsRemaining,
    guestsRemaining,
    recipesRemaining,
    inventoryRemaining,
    groupsRemaining,
    shoppingListsRemaining,
    wishlistRemaining,
    
    // Estado
    isLoading,
    usage,
    
    // Ações
    refreshLimits,
    showUpgradePrompt,
    
    // Funções de verificação assíncrona
    checkEventLimit,
    checkGuestLimit,
    checkRecipeLimit,
    checkInventoryLimit,
    featureLimits: planLimits,
  };
};
